/**
 * Exotel Voice API — Frontend Service
 * Wraps all CRM ↔ backend Exotel API calls.
 */
import api from './api';

/**
 * Initiate an outbound click-to-call from the CRM.
 * Exotel will call the agent's registered phone first, then bridge to the patient.
 *
 * @param {string} leadId    - MongoDB Lead _id
 * @param {string} toNumber  - Patient's phone number (optional — falls back to lead.patientPhone)
 * @returns {Promise<{ callLogId, callSid, status, from, to }>}
 */
export const initiateCall = async (leadId, toNumber = null) => {
  const body = { leadId };
  if (toNumber) body.toNumber = toNumber;
  const response = await api.post('/exotel/call', body);
  return response.data;
};

/**
 * Fetch call history for a specific lead.
 *
 * @param {string} leadId
 * @param {number} [limit=50]
 * @returns {Promise<Array>} Array of CallLog objects
 */
export const getCallLogs = async (leadId, limit = 50) => {
  const response = await api.get(`/exotel/calls/${leadId}`, { params: { limit } });
  return response.data?.data || [];
};

/**
 * Fetch all call logs (admin).
 *
 * @param {{ direction?: string, status?: string, limit?: number }} params
 * @returns {Promise<Array>}
 */
export const getAllCallLogs = async (params = {}) => {
  const response = await api.get('/exotel/calls', { params });
  return response.data?.data || [];
};

/**
 * Save call query reason and notes.
 *
 * @param {string} idOrSid - CallLog _id or callSid
 * @param {{ callReason: string, notes?: string }} payload
 * @returns {Promise<Object>}
 */
export const saveCallQuery = async (idOrSid, payload) => {
  const response = await api.patch(`/exotel/calls/${idOrSid}/query`, payload);
  return response.data;
};

/**
 * Save CRM call disposition & callback resolution.
 *
 * @param {string} idOrSid - CallLog _id or callSid
 * @param {{ disposition: string, dispositionNotes?: string, notes?: string, callReason?: string }} payload
 * @returns {Promise<Object>}
 */
export const saveCallDisposition = async (idOrSid, payload) => {
  const response = await api.patch(`/exotel/calls/${idOrSid}/disposition`, payload);
  return response.data;
};

